import { useRegisterSW } from "virtual:pwa-register/react";
import { RefreshCw } from "lucide-react";

/**
 * PWA update banner. When `vite-plugin-pwa` detects a new bundle on the
 * server, this banner renders. Tap → calls `updateServiceWorker(true)`,
 * which activates the new SW and reloads the page.
 *
 * The banner also nudges the SW to check for updates every 5 minutes so
 * Cloudflare Pages deploys land within ~5 min for already-installed PWAs.
 */
export function PWAUpdateBanner() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(swReg) {
      if (!swReg) return;
      // Check for updates every 5 min while the tab is open.
      setInterval(
        () => {
          swReg.update().catch(() => {
            /* offline or transient — try next tick */
          });
        },
        5 * 60 * 1000,
      );
    },
    onRegisterError(error) {
      console.warn("[pwa] sw registration failed", error);
    },
  });

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: "50%",
        bottom: "calc(env(safe-area-inset-bottom) + 20px)",
        transform: "translateX(-50%)",
        zIndex: 100,
        background: "var(--bg-3)",
        border: "1px solid var(--accent)",
        boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-3) var(--space-4)",
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        fontSize: "var(--type-body-s)",
        color: "var(--fg)",
        maxWidth: "calc(100vw - 32px)",
      }}
    >
      <RefreshCw size={16} strokeWidth={1.5} color="var(--accent)" aria-hidden />
      <span>Update available.</span>
      <button
        type="button"
        onClick={() => {
          updateServiceWorker(true);
        }}
        style={{
          background: "var(--accent)",
          color: "var(--accent-on)",
          border: "none",
          padding: "6px 12px",
          borderRadius: 8,
          fontSize: "var(--type-body-s)",
          cursor: "pointer",
        }}
      >
        Reload
      </button>
    </div>
  );
}
